import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import TuneIcon from '@mui/icons-material/Tune';
import { useField, useFormikContext } from "formik";

export type VideoH264AdvancedSettings = {
  bFrames: number | "";
  bitrateMode: "cbr" | "vbr";
  referenceFrame: number | "";
  keyIntervalMax: number | "";
  speedPreset: "ultrafast" | "superfast" | "veryfast" | "faster" | "fast" | "medium" | "slow" | "slower" | "veryslow";
  tune: "none" | "film" | "animation" | "grain" | "zerolatency";
  aud: boolean;
  cabac: boolean;
  intraRefresh: boolean;
};

export type VideoVp8AdvancedSettings = {
  cpuUsed: number;
  cqLevel: number;
  deadline: number;
  threads: number;
  lagInFrames: number;
  autoAltRef: boolean;
  errorResilient: number;
  keyframeMaxDist: number;
  endUsage: number;
  resizeAllowed: boolean;
  resizeDownThreshold: number;
  resizeUpThreshold: number;
};

export type VideoVp9AdvancedSettings = {
  cpuUsed: number;
  cqLevel: number;
  deadline: number;
  threads: number;
  lagInFrames: number;
  autoAltRef: boolean;
  errorResilient: number;
  keyframeMaxDist: number;
  endUsage: number;
  resizeAllowed: boolean;
  resizeDownThreshold: number;
  resizeUpThreshold: number;
  rowMt: boolean;
};

export type VideoAdvancedSettingsByCodec = {
  h264: VideoH264AdvancedSettings;
  vp8: VideoVp8AdvancedSettings;
  vp9: VideoVp9AdvancedSettings;
};

export type VideoParamsFormValues = {
  resolution: string;
  frameRate: number | "";
  codec: "h264" | "vp8" | "vp9";
  bitrate: number | "";
  advanced: VideoAdvancedSettingsByCodec;
};

export const DEFAULT_VIDEO_BITRATE = 4000;
export const DESKTOP_DEFAULT_VIDEO_BITRATE = 10000;

type CreateDefaultVideoParamsOptions = {
  bitrate?: number;
};

export const createDefaultVideoH264AdvancedSettings = (): VideoH264AdvancedSettings => ({
  bFrames: 0,
  bitrateMode: "cbr",
  referenceFrame: 3,
  keyIntervalMax: 30,
  speedPreset: "fast",
  tune: "zerolatency",
  aud: true,
  cabac: true,
  intraRefresh: false
});

export const createDefaultVideoVp8AdvancedSettings = (): VideoVp8AdvancedSettings => ({
  cpuUsed: 1,
  cqLevel: 10,
  deadline: 1_000_000,
  threads: 0,
  lagInFrames: 25,
  autoAltRef: true,
  errorResilient: 0,
  keyframeMaxDist: 128,
  endUsage: 0,
  resizeAllowed: false,
  resizeDownThreshold: 30,
  resizeUpThreshold: 60
});

export const createDefaultVideoVp9AdvancedSettings = (): VideoVp9AdvancedSettings => ({
  cpuUsed: 1,
  cqLevel: 10,
  deadline: 1_000_000,
  threads: 0,
  lagInFrames: 25,
  autoAltRef: true,
  errorResilient: 0,
  keyframeMaxDist: 128,
  endUsage: 0,
  resizeAllowed: false,
  resizeDownThreshold: 30,
  resizeUpThreshold: 60,
  rowMt: false
});

export const createDefaultVideoAdvancedSettings = (): VideoAdvancedSettingsByCodec => ({
  h264: createDefaultVideoH264AdvancedSettings(),
  vp8: createDefaultVideoVp8AdvancedSettings(),
  vp9: createDefaultVideoVp9AdvancedSettings()
});

export const createDefaultVideoParams = (options?: CreateDefaultVideoParamsOptions): VideoParamsFormValues => ({
  resolution: "No scaling",
  frameRate: 30,
  codec: "vp8",
  bitrate: options?.bitrate ?? DEFAULT_VIDEO_BITRATE,
  advanced: createDefaultVideoAdvancedSettings()
});

type VideoParamsProps = {
  name?: string;
  mode?: "view" | "edit";
};

const RESOLUTION_OPTIONS = [
  "No scaling",
  "3840x2160",
  "2560x1440",
  "1920x1080",
  "1600x900",
  "1280x720",
  "1024x576",
  "960x540",
  "854x480",
  "720x480",
  "640x480",
  "640x360",
  "320x256",
  "256x144"
] as const;

export const VIDEO_CODEC_LABELS: Record<VideoParamsFormValues["codec"], string> = {
  h264: "H264",
  vp8: "VP8",
  vp9: "VP9"
};

export const VIDEO_CODEC_OPTIONS: { value: VideoParamsFormValues["codec"]; label: string }[] = [
  { value: "h264", label: VIDEO_CODEC_LABELS.h264 },
  { value: "vp8", label: VIDEO_CODEC_LABELS.vp8 },
  { value: "vp9", label: VIDEO_CODEC_LABELS.vp9 }
];

const BITRATE_MODE_LABELS: Record<VideoH264AdvancedSettings["bitrateMode"], string> = {
  cbr: "Constant Bitrate Encoding",
  vbr: "Variable Bitrate Encoding"
};

const SPEED_PRESET_LABELS: Record<VideoH264AdvancedSettings["speedPreset"], string> = {
  ultrafast: "Ultrafast",
  superfast: "Superfast",
  veryfast: "Very fast",
  faster: "Faster",
  fast: "Fast",
  medium: "Medium",
  slow: "Slow",
  slower: "Slower",
  veryslow: "Very slow"
};

const TUNE_LABELS: Record<VideoH264AdvancedSettings["tune"], string> = {
  none: "None",
  film: "Film",
  animation: "Animation",
  grain: "Grain",
  zerolatency: "Zero latency"
};

const VideoParamsBase = ({ name = "video", mode = "edit" }: VideoParamsProps) => {
  const readOnly = mode === "view";
  const { setFieldValue } = useFormikContext<unknown>();
  const [isAdvancedOpen, setAdvancedOpen] = useState(false);

  const [resolutionField, resolutionMeta] = useField<string>({ name: `${name}.resolution` });
  const [frameRateField, frameRateMeta, frameRateHelpers] = useField<number | "">({ name: `${name}.frameRate` });
  const [codecField] = useField<VideoParamsFormValues["codec"]>({ name: `${name}.codec` });
  const [bitrateField, bitrateMeta, bitrateHelpers] = useField<number | "">({ name: `${name}.bitrate` });

  const [bFramesField, bFramesMeta, bFramesHelpers] = useField<number | "">({
    name: `${name}.advanced.h264.bFrames`
  });
  const [bitrateModeField, bitrateModeMeta] = useField<VideoH264AdvancedSettings["bitrateMode"]>({
    name: `${name}.advanced.h264.bitrateMode`
  });
  const [referenceFrameField, referenceFrameMeta, referenceFrameHelpers] = useField<number | "">({
    name: `${name}.advanced.h264.referenceFrame`
  });
  const [keyIntervalMaxField, keyIntervalMaxMeta, keyIntervalMaxHelpers] = useField<number | "">({
    name: `${name}.advanced.h264.keyIntervalMax`
  });
  const [speedPresetField, speedPresetMeta] = useField<VideoH264AdvancedSettings["speedPreset"]>({
    name: `${name}.advanced.h264.speedPreset`
  });
  const [tuneField, tuneMeta] = useField<VideoH264AdvancedSettings["tune"]>({ name: `${name}.advanced.h264.tune` });
  const [audField, , audHelpers] = useField<boolean>({ name: `${name}.advanced.h264.aud`, type: "checkbox" });
  const [cabacField, , cabacHelpers] = useField<boolean>({ name: `${name}.advanced.h264.cabac`, type: "checkbox" });
  const [intraRefreshField, , intraRefreshHelpers] = useField<boolean>({
    name: `${name}.advanced.h264.intraRefresh`,
    type: "checkbox"
  });

  const openAdvanced = () => setAdvancedOpen(true);
  const closeAdvanced = () => setAdvancedOpen(false);
  const showBitrateAndAdvanced = codecField.value === "h264";

  useEffect(() => {
    if (!showBitrateAndAdvanced && isAdvancedOpen) {
      setAdvancedOpen(false);
    }
  }, [showBitrateAndAdvanced, isAdvancedOpen]);

  const resetAdvancedToDefaults = () => {
    if (readOnly) return;
    const defaults = createDefaultVideoH264AdvancedSettings();
    Object.entries(defaults).forEach(([key, value]) => {
      void setFieldValue(`${name}.advanced.h264.${key}`, value, true);
    });
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          {...resolutionField}
          label="Resolution"
          select
          fullWidth
          disabled={readOnly}
          error={resolutionMeta.touched && Boolean(resolutionMeta.error)}
          helperText={resolutionMeta.touched ? resolutionMeta.error : undefined}
        >
          {RESOLUTION_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          name={frameRateField.name}
          label="Frame rate (fps, -1 uses source)"
          type="number"
          fullWidth
          disabled={readOnly}
          value={frameRateField.value === "" ? "" : frameRateField.value}
          onChange={(event) => {
            const next = event.target.value;
            frameRateHelpers.setValue(next === "" ? "" : Number(next));
          }}
          onBlur={frameRateField.onBlur}
          error={frameRateMeta.touched && Boolean(frameRateMeta.error)}
          helperText={frameRateMeta.touched ? frameRateMeta.error : undefined}
          slotProps={{ htmlInput: { min: -1, max: 60, step: 1 } }}
        />
      </Stack>
      {showBitrateAndAdvanced ? (
        <>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              name={bitrateField.name}
              label="Bitrate (Kb/s)"
              type="number"
              fullWidth
              disabled={readOnly}
              value={bitrateField.value === "" ? "" : bitrateField.value}
              onChange={(event) => {
                const next = event.target.value;
                bitrateHelpers.setValue(next === "" ? "" : Number(next));
              }}
              onBlur={bitrateField.onBlur}
              error={bitrateMeta.touched && Boolean(bitrateMeta.error)}
              helperText={bitrateMeta.touched ? bitrateMeta.error : undefined}
              slotProps={{ htmlInput: { min: 1, max: 100000, step: 1 } }}
            />
          </Stack>
          <Box>
            <Button
              type="button"
              variant="text"
              size="small"
              startIcon={<SettingsSuggestIcon fontSize="small" />}
              onClick={openAdvanced}
              sx={{ textTransform: "none", px: 0 }}
            >
              Advanced Settings
            </Button>
            <Tooltip title="Adjust codec tuning parameters" placement="right">
              <Typography variant="caption" component="div" color="text.secondary">
                Customize encoder behavior such as presets, keyframes, and entropy coding.
              </Typography>
            </Tooltip>
          </Box>

          <Dialog open={isAdvancedOpen} onClose={closeAdvanced} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
             <Box display="inline-flex" alignItems="center" gap={1}>
              <TuneIcon sx={{ color: 'text.secondary' }} />
              <Typography variant="h5" sx={{ m: 0 }}>
                   Advanced Settings - {VIDEO_CODEC_LABELS[codecField.value] ?? codecField.value.toUpperCase()}
              </Typography>
            </Box>             
              <IconButton onClick={closeAdvanced} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={3}>
                <Box
                  display="grid"
                  gap={2}
                  gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(2, minmax(0, 1fr))" }}
                >
                  <TextField
                    name={bFramesField.name}
                    label="B-Frames"
                    type="number"
                    fullWidth
                    disabled={readOnly}
                    value={bFramesField.value === "" ? "" : bFramesField.value}
                    onChange={(event) => {
                      const next = event.target.value;
                      bFramesHelpers.setValue(next === "" ? "" : Number(next));
                    }}
                    onBlur={bFramesField.onBlur}
                    error={bFramesMeta.touched && Boolean(bFramesMeta.error)}
                    helperText={bFramesMeta.touched ? bFramesMeta.error : undefined}
                    slotProps={{ htmlInput: { min: 0, max: 16, step: 1 } }}
                  />
                  <TextField
                    {...bitrateModeField}
                    label="Bitrate Mode"
                    select
                    fullWidth
                    disabled={readOnly}
                    error={bitrateModeMeta.touched && Boolean(bitrateModeMeta.error)}
                    helperText={bitrateModeMeta.touched ? bitrateModeMeta.error : undefined}
                  >
                    {Object.entries(BITRATE_MODE_LABELS).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    {...speedPresetField}
                    label="Speed Preset"
                    select
                    fullWidth
                    disabled={readOnly}
                    error={speedPresetMeta.touched && Boolean(speedPresetMeta.error)}
                    helperText={speedPresetMeta.touched ? speedPresetMeta.error : undefined}
                  >
                    {Object.entries(SPEED_PRESET_LABELS).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    {...tuneField}
                    label="Tune"
                    select
                    fullWidth
                    disabled={readOnly}
                    error={tuneMeta.touched && Boolean(tuneMeta.error)}
                    helperText={tuneMeta.touched ? tuneMeta.error : undefined}
                  >
                    {Object.entries(TUNE_LABELS).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box
                  display="grid"
                  gap={2}
                  gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(2, minmax(0, 1fr))" }}
                >
                  <TextField
                    name={referenceFrameField.name}
                    label="Reference Frame"
                    type="number"
                    fullWidth
                    disabled={readOnly}
                    value={referenceFrameField.value === "" ? "" : referenceFrameField.value}
                    onChange={(event) => {
                      const next = event.target.value;
                      referenceFrameHelpers.setValue(next === "" ? "" : Number(next));
                    }}
                    onBlur={referenceFrameField.onBlur}
                    error={referenceFrameMeta.touched && Boolean(referenceFrameMeta.error)}
                    helperText={referenceFrameMeta.touched ? referenceFrameMeta.error : undefined}
                    slotProps={{ htmlInput: { min: 0, max: 16, step: 1 } }}
                  />
                  <TextField
                    name={keyIntervalMaxField.name}
                    label="Key Interval Max"
                    type="number"
                    fullWidth
                    disabled={readOnly}
                    value={keyIntervalMaxField.value === "" ? "" : keyIntervalMaxField.value}
                    onChange={(event) => {
                      const next = event.target.value;
                      keyIntervalMaxHelpers.setValue(next === "" ? "" : Number(next));
                    }}
                    onBlur={keyIntervalMaxField.onBlur}
                    error={keyIntervalMaxMeta.touched && Boolean(keyIntervalMaxMeta.error)}
                    helperText={keyIntervalMaxMeta.touched ? keyIntervalMaxMeta.error : undefined}
                    slotProps={{ htmlInput: { min: 0, max: 360, step: 1 } }}
                  />
                </Box>
                <Stack direction="row" spacing={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(audField.value)}
                        onChange={(event) => audHelpers.setValue(event.target.checked)}
                        onBlur={audField.onBlur}
                        disabled={readOnly}
                      />
                    }
                    label="AUD"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(cabacField.value)}
                        onChange={(event) => cabacHelpers.setValue(event.target.checked)}
                        onBlur={cabacField.onBlur}
                        disabled={readOnly}
                      />
                    }
                    label="CABAC"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(intraRefreshField.value)}
                        onChange={(event) => intraRefreshHelpers.setValue(event.target.checked)}
                        onBlur={intraRefreshField.onBlur}
                        disabled={readOnly}
                      />
                    }
                    label="Intra Refresh"
                  />
                </Stack>
                {!readOnly ? (
                  <Box>
                    <Button variant="outlined" size="small" onClick={resetAdvancedToDefaults}>
                      Default settings
                    </Button>
                  </Box>
                ) : null}
              </Stack>
            </DialogContent>
          </Dialog>
        </>
      ) : null}
    </Stack>
  );
};

export const VideoParamsH264 = (props: VideoParamsProps) => <VideoParamsBase {...props} />;
export const VideoParamsVp8 = (props: VideoParamsProps) => <VideoParamsBase {...props} />;
export const VideoParamsVp9 = (props: VideoParamsProps) => <VideoParamsBase {...props} />;
